import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, Paper, Chip, Button, Grid, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { useNavigate } from 'react-router-dom';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import { useAuth } from '../context/AuthContext';
import { getUserOrders, updateOrderStatus } from '../services/api';
import { formatPrice, formatDate } from '../utils/formatPrice';
import toast from 'react-hot-toast';

const statusColors = {
  Pending: { bg: '#fff3e0', color: '#e65100' },
  Accepted: { bg: '#e3f2fd', color: '#1565c0' },
  Shipped: { bg: '#e8f5e9', color: '#2e7d32' },
  Delivered: { bg: '#e8f5e9', color: '#2e7d32' },
  Completed: { bg: '#e8f5e9', color: '#2e7d32' },
  Cancelled: { bg: '#ffebee', color: '#c62828' },
  Rejected: { bg: '#ffebee', color: '#c62828' },
};

const Order = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // States for Track & Cancel
  const [openTrack, setOpenTrack] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!isAuthenticated) {
          navigate('/');
          return;
        }
        const userId = localStorage.getItem('UserId');
        if (!userId) {
          setOrders([]);
          return;
        }
        const response = await getUserOrders(userId);
        const ordersData = response?.data?.data || response?.data?.orders || response?.data || [];
        setOrders(Array.isArray(ordersData) ? ordersData : []);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [isAuthenticated, navigate]);

  // --- Cancel Logic Functions ---
  const triggerCancelModal = (orderId) => {
    setOrderToCancel(orderId);
    setOpenConfirm(true);
  };

  const confirmCancel = async () => {
    const loadingToast = toast.loading("Cancelling order...");
    try {
      await updateOrderStatus({ 
        id: orderToCancel, 
        Statusmsg: 'Cancelled',
        reason: cancelReason // Backend supports reason
      });

      setOrders(prev => prev.map(order => 
        order._id === orderToCancel ? { ...order, Status: 'Cancelled' } : order
      ));
      
      toast.success('Order cancelled successfully', { id: loadingToast });
      setOpenConfirm(false);
      setCancelReason("");
    } catch (error) {
      toast.error('Failed to cancel order', { id: loadingToast });
    }
  };

  const handleOpenTrack = (id) => {
    setSelectedOrderId(id.slice(-8).toUpperCase());
    setOpenTrack(true);
  };

  const handleWhatsAppTrack = () => {
    const phoneNumber = "91XXXXXXXXXX"; // Apna number update karein
    const message = `Hi, I want to track my order. Order ID: #${selectedOrderId}`;
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) return <Loader message="Loading your orders..." />;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fcfcfc' }}>
      <Navbar />
      <Container maxWidth="md" sx={{ py: 5 }}>
        <Breadcrumb items={[{ label: 'My Orders' }]} />
        <Typography variant="h4" fontWeight="800" color="#1a1a1a" sx={{ mb: 4, mt: 2 }}>
          My Orders
        </Typography>

        {orders.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {orders.map((order) => (
              <Paper
                key={order._id}
                elevation={0}
                sx={{ 
                  borderRadius: 4, overflow: 'hidden', border: '1px solid #eee', transition: '0.3s',
                  '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }
                }}
              >
                {/* Header */}
                <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="caption" fontWeight="700" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      Order ID: #{order._id?.slice(-8).toUpperCase()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight="500">
                      Placed: {formatDate(order.created_at)}
                    </Typography>
                  </Box>
                  <Chip
                    label={order.Status || "Pending"}
                    size="small"
                    sx={{
                      bgcolor: statusColors[order.Status]?.bg || '#f5f5f5',
                      color: statusColors[order.Status]?.color || '#666',
                      fontWeight: '800', borderRadius: '6px'
                    }}
                  />
                </Box>

                {/* Products */}
                <Box sx={{ p: 2.5 }}>
                  {order.Products?.map((product, idx) => (
                    <Grid container spacing={2} key={idx} sx={{ mb: idx !== order.Products.length - 1 ? 2 : 0, alignItems: 'center' }}>
                      <Grid item xs={3} sm={2}>
                        <img 
                          src={product.ProductMainImgUrl} 
                          alt={product.ProductName} 
                          style={{ width: '100%', borderRadius: '12px', aspectRatio: '1/1', objectFit: 'cover', border: '1px solid #f0f0f0' }} 
                        />
                      </Grid>
                      <Grid item xs={9} sm={6}>
                        <Typography variant="subtitle1" fontWeight="700" sx={{ color: '#333', lineHeight: 1.2 }}>{product.ProductName}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          Qty: {product.ProductQuantity} | Size: {product.ProductSize || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="subtitle1" fontWeight="800" color="#2e7d32">
                          Rs.{formatPrice(product.ProductPrice * product.ProductQuantity)}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Box>

                <Divider sx={{ borderStyle: 'dashed' }} />

                {/* Footer / Actions */}
                <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    {order.discountAmount > 0 && (
                        <Typography variant="caption" fontWeight="bold" color="error.main" sx={{ display: 'block' }}>
                            Discount: -Rs.{formatPrice(order.discountAmount)}
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6" fontWeight="900" color="#1a1a1a">
                      Rs.{formatPrice(order.TotalAmount)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    {(order.Status === 'Completed' || order.Status === 'Delivered') ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#e8f5e9', px: 2.5, py: 1, borderRadius: '50px', border: '1px solid #a5d6a7' }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#2e7d32' }} />
                        <Typography variant="body2" fontWeight="700" color="#2e7d32">Success! ❤️</Typography>
                      </Box>
                    ) : (
                      <>
                        {order.Status !== 'Cancelled' && (
                            <Button 
                            variant="contained" 
                            disableElevation
                            startIcon={<LocalShippingIcon />}
                            onClick={() => handleOpenTrack(order._id)}
                            sx={{ bgcolor: '#3E2723', '&:hover': { bgcolor: '#1a1a1a' }, borderRadius: '10px', textTransform: 'none', fontWeight: '600' }}
                            >
                            Track
                            </Button>
                        )}

                        {(order.Status === 'Pending' || order.Status === 'pending') && (
                          <Button 
                            variant="outlined" 
                            color="error" 
                            onClick={() => triggerCancelModal(order._id)}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: '600' }}
                          >
                            Cancel Order
                          </Button>
                        )}
                        {order.Status === 'Cancelled' && (
                             <Typography variant="body2" fontWeight="bold" color="error">CANCELLED</Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 4, border: '1px solid #eee' }}>
            <ShoppingBagIcon sx={{ fontSize: 100, color: '#f0f0f0', mb: 2 }} />
            <Typography variant="h5" fontWeight="700" color="#555">No orders yet</Typography>
            <Button onClick={() => navigate('/filter/All')} sx={{ mt: 3, bgcolor: '#876513', color: 'white', '&:hover': { bgcolor: '#6d3610' }, px: 4 }}>Start Shopping</Button>
          </Paper>
        )}
      </Container>

      {/* TRACK MODAL */}
      <Dialog open={openTrack} onClose={() => setOpenTrack(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: '800', textAlign: 'center' }}>
          Track Order
          <IconButton onClick={() => setOpenTrack(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
           <Typography variant="body2" color="text.secondary" mb={2}>Order ID: #{selectedOrderId}</Typography>
           <Button fullWidth variant="contained" startIcon={<WhatsAppIcon />} onClick={handleWhatsAppTrack} sx={{ bgcolor: '#25D366', py: 1.5, borderRadius: '12px' }}>Track on WhatsApp</Button>
        </DialogContent>
      </Dialog>

      {/* CANCEL CONFIRMATION MODAL (THE NEW DIV) */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: '800' }}>Confirm Cancellation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>"Are you sure you want to cancel this order?</Typography>
          <TextField
            fullWidth multiline rows={2}
            label="Reason (Optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenConfirm(false)} color="inherit">No</Button>
          <Button variant="contained" color="error" onClick={confirmCancel}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </Box>
  );
};

export default Order;